import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

type Props = {
  onBase64Change?: (base64: string) => void;
  multiple?: boolean;
  showSelectedPreview?: boolean;
};

export type ImageUploaderHandle = {
  openFileDialog: () => void;
  clearImage: () => void;
};

const ImageUploader = forwardRef<ImageUploaderHandle, Props>(({ onBase64Change, multiple = false, showSelectedPreview = true }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fileToDataUrl = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('failed to read file'));
      };
      reader.onerror = () => reject(reader.error ?? new Error('failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const encodedList = await Promise.all(files.map((file) => fileToDataUrl(file)));
    const lastImage = encodedList[encodedList.length - 1];
    setPreviewUrl(lastImage);
    if (onBase64Change) {
      encodedList.forEach((base64) => onBase64Change(base64));
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onBase64Change]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) {
      handleFiles(multiple ? files : [files[0]]).catch(() => {});
    }
  }, [multiple, handleFiles]);

  const onClick = (isClear: boolean = false) => {
    if (isClear) {
      setPreviewUrl(null);
      if (onBase64Change) {
        onBase64Change('');
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    } else {
      inputRef.current?.click();
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) handleFiles(multiple ? files : [files[0]]).catch(() => {});
    else if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useImperativeHandle(ref, () => ({
    openFileDialog: () => onClick(false),
    clearImage: () => onClick(true)
  }));

  return (
    <div
      onClick={() => onClick(false)}
      onDrop={onDrop} 
      onDragOver={(e) => e.preventDefault()}
      className={`relative w-full sm:w-[500px] border-[1px] border-dashed border-gray-400 rounded-lg flex items-center justify-center cursor-pointer ${(showSelectedPreview && previewUrl) ? 'h-[200px]' : 'h-10'}`}
    >
      {showSelectedPreview && previewUrl ? (
        <img src={previewUrl} alt="preview" className="max-h-full max-w-full object-contain my-2" />
      ) : (
        <p className="text-gray-500">画像をアップロード</p>
      )}
      {showSelectedPreview && previewUrl && (
        <div
          id="clear-image"
          style={{ position: 'absolute', top: 4, right: 4 }}
          onClick={(e) => {
            e.stopPropagation();
            onClick(true);
          }}
          className="text-gray-500"
        >
          <img src="/images/icon-close.svg" alt="clear" className="w-6 h-6" />
        </div>
      )}
      <input
        
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
});

export default ImageUploader;
